import { useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, useNavigate } from '@tanstack/react-router';
import { type FormEvent, useEffect, useState } from 'react';
import { api, ApiError, call, type Schemas } from '../../api/client';
import { useSession } from '../../api/session';
import { Button, ErrorText, Field, Input, Select, Spinner } from '../../components/ui';
import { localeWeekStart } from '../../lib/format';
import { takeHashToken } from '../../lib/hash-token';
import { ProviderButtons, useSignInOptions } from '../auth/SignInOptions';
import { AuthFrame } from '../auth/AuthPages';

export const ADULT_LABELS = [
  ['', 'No label'],
  ['MOM', 'Mom'],
  ['DAD', 'Dad'],
  ['MOTHER', 'Mother'],
  ['FATHER', 'Father'],
  ['PARENT', 'Parent'],
  ['PARTNER', 'Partner'],
  ['GUARDIAN', 'Guardian'],
  ['OTHER', 'Other…'],
] as const;

export function CreateFamilyPage() {
  const { status, household, me, reloadMe } = useSession();
  const navigate = useNavigate();
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState('');

  if (status === 'loading') return <Spinner />;
  if (status === 'anonymous') return <Navigate to="/login" search={{ redirect: '/onboarding' }} />;
  if (household) return <Navigate to="/" />;

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setBusy(true);
    setError(null);
    try {
      await call(
        api.POST('/api/v1/families', {
          body: {
            name: String(f.get('name')),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            weekStartsOn: localeWeekStart(),
            ...(label ? { relationshipLabelCode: label as 'MOM' } : {}),
            ...(label === 'OTHER' ? { relationshipLabelCustom: String(f.get('custom')) } : {}),
          },
        }),
      );
      await reloadMe();
      await navigate({ to: '/settings' });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  if (me && !me.emailVerified) {
    return (
      <AuthFrame title="Confirm your email first">
        <p className="mb-4 text-sm text-[var(--muted)]">We sent a confirmation link to {me.email}. Open it, then come back here.</p>
        <Button variant="secondary" className="w-full" onClick={() => void call(api.POST('/api/v1/auth/resend-verification', { body: { email: me.email } }))}>
          Resend the link
        </Button>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame title="Set up your household">
      <form className="space-y-4" onSubmit={(e) => void submit(e)}>
        <Field label="Household name" hint="For example: The Smiths">
          {(id) => <Input id={id} name="name" required maxLength={80} placeholder="The Smiths" />}
        </Field>
        <Field label="What do the kids call you? (optional)">
          {(id) => (
            <Select id={id} value={label} onChange={(e) => setLabel(e.target.value)}>
              {ADULT_LABELS.map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </Select>
          )}
        </Field>
        {label === 'OTHER' ? <Field label="Label">{(id) => <Input id={id} name="custom" maxLength={30} required />}</Field> : null}
        <ErrorText error={error} />
        <Button type="submit" className="w-full" busy={busy}>
          Create household
        </Button>
        <p className="text-xs text-[var(--muted)]">Next you can invite your partner and add your children. Your partner has the same say over household work as you.</p>
      </form>
    </AuthFrame>
  );
}

const INVITE_KEY = 'familychores.invite';

export function AcceptInvitePage() {
  const { status, household, reloadMe } = useSession();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [token] = useState(() => {
    const fromHash = takeHashToken();
    if (fromHash) sessionStorage.setItem(INVITE_KEY, fromHash); // survive a sign-up/sign-in detour
    return fromHash ?? sessionStorage.getItem(INVITE_KEY);
  });
  const signInOptions = useSignInOptions().data;
  const [preview, setPreview] = useState<Schemas['InvitationPreviewResponse'] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    void call(api.POST('/api/v1/invitations/preview', { body: { token } })).then(setPreview, (err: unknown) => {
      // A dead invitation shouldn't linger in the tab's storage.
      if (err instanceof ApiError && err.code === 'INVITATION_INVALID') sessionStorage.removeItem(INVITE_KEY);
      setError(err);
    });
  }, [token]);

  if (!token || (error instanceof ApiError && error.code === 'INVITATION_INVALID')) {
    return (
      <AuthFrame title="Invitation not valid">
        <p className="text-sm text-[var(--muted)]">This invitation has expired or was already used. Ask for a new one.</p>
      </AuthFrame>
    );
  }
  if (!preview) return <Spinner />;

  const accept = async () => {
    setBusy(true);
    setError(null);
    try {
      await call(api.POST('/api/v1/invitations/accept', { body: { token } }));
      sessionStorage.removeItem(INVITE_KEY);
      qc.clear();
      await reloadMe();
      await navigate({ to: '/' });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthFrame title={`Join ${preview.familyName}`}>
      <p className="mb-4 text-sm text-[var(--muted)]">
        {preview.invitedEmail ? (
          <>{preview.inviterName} invited <strong className="text-[var(--text)]">{preview.invitedEmail}</strong> to share household work on FamilyChores.</>
        ) : (
          <>{preview.inviterName} invited you to share household work on FamilyChores.</>
        )}
      </p>
      {status === 'authenticated' ? (
        household ? (
          <p className="text-sm">You already belong to a household. Leave it first to join this one.</p>
        ) : (
          <Button className="w-full" busy={busy} onClick={() => void accept()}>
            Join household
          </Button>
        )
      ) : (
        <div className="space-y-2">
          <ProviderButtons returnTo="/invite" />
          {signInOptions?.password ? (
            <>
              <Link to="/register"><Button variant="secondary" className="w-full">Create an account with email</Button></Link>
              <Link to="/login" search={{ redirect: '/invite' }}><Button variant="ghost" className="w-full">Sign in with email</Button></Link>
            </>
          ) : null}
          {preview.invitedEmail ? <p className="text-xs text-[var(--muted)]">Use the account with the email address the invitation was sent to.</p> : null}
        </div>
      )}
      <div className="mt-3"><ErrorText error={error} /></div>
    </AuthFrame>
  );
}
